import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

        const searchParams = new URLSearchParams(location.search);

        // Server-side OAuth flow: backend redirects here with ?token=SESSION_TOKEN&u=BASE64_USER
        // User data is embedded directly — avoids cross-origin /auth/me call that Safari blocks
        const token = searchParams.get('token');
        const userB64 = searchParams.get('u');
        if (token && userB64) {
          try {
            const userData = JSON.parse(atob(userB64));
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(userData));
            toast.success(`Welcome, ${userData.name || userData.full_name || 'back'}!`);
            await new Promise(resolve => setTimeout(resolve, 300));
            window.location.href = '/browse';
            return;
          } catch (e) {
            console.error('Failed to parse user data:', e);
          }
        }

        // Fallback: token only — try /auth/me
        if (token) {
          localStorage.setItem('token', token);
          const userRes = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          localStorage.setItem('user', JSON.stringify(userRes.data));
          toast.success(`Welcome, ${userRes.data.name || userRes.data.full_name || 'back'}!`);
          await new Promise(resolve => setTimeout(resolve, 300));
          window.location.href = '/browse';
          return;
        }

        // Check for error from Google or backend
        const googleError = searchParams.get('error');
        if (googleError) {
          if (googleError === 'access_denied') {
            toast.error('Access denied. Make sure your Google account is added as a test user.');
          } else {
            toast.error(`Sign-in error: ${googleError}`);
          }
          navigate('/login');
          return;
        }

        // Legacy Emergent auth: session_id comes in URL fragment #session_id=...
        const hash = location.hash.substring(1);
        const hashParams = new URLSearchParams(hash);
        const sessionId = hashParams.get('session_id');
        if (sessionId) {
          const response = await axios.post(
            `${API}/auth/google/callback`,
            { session_id: sessionId }
          );
          const userData = response.data.user;
          localStorage.setItem('user', JSON.stringify(userData));
          toast.success(`Welcome, ${userData.name || userData.full_name}!`);
          await new Promise(resolve => setTimeout(resolve, 300));
          window.location.href = '/browse';
          return;
        }

        toast.error('Authentication failed - please try again');
        navigate('/login');
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error('Authentication failed - please try again');
        navigate('/login');
      }
    };

    processAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-white text-lg">Completing sign in...</p>
      </div>
    </div>
  );
}